import { z } from 'zod';

import type { IssuePresentation } from '@/features/audit-issues/types';

/** spec-05 §7 reconciliation result classification */
export const reconciliationResultValues = ['blocked', 'ready_with_warnings', 'ready'] as const;
export type ReconciliationResultLabel = (typeof reconciliationResultValues)[number];

export const exceptionSeverityValues = ['info', 'warning', 'error', 'critical'] as const;
export type ExceptionSeverity = (typeof exceptionSeverityValues)[number];

export const exceptionStatusValues = ['open', 'resolved', 'waived', 'rejected', 'closed'] as const;
export type ExceptionStatus = (typeof exceptionStatusValues)[number];

export const exceptionReportSchema = z.object({
  exception_id: z.string(),
  monthly_batch_id: z.string(),
  exception_code: z.string(),
  severity: z.enum(exceptionSeverityValues),
  status: z.enum(exceptionStatusValues),
  description: z.string(),
  // FR-007 reporter context + supplier tri-state reply + platform decision
  // (migration 0007). Optional so older payload snapshots keep parsing.
  cause: z.string().nullable().optional(),
  affects_quotation: z.boolean().optional(),
  additional_cost: z.boolean().optional(),
  supplier_reply: z
    .enum(['return_requested', 'onsite_processing_authorized', 'more_info_requested'])
    .nullable()
    .optional(),
  supplier_reply_note: z.string().nullable().optional(),
  supplier_replied_by: z.string().nullable().optional(),
  supplier_replied_at: z.string().nullable().optional(),
  decision: z.enum(['return', 'onsite_processing', 'no_action', 'other']).nullable().optional(),
  decision_note: z.string().nullable().optional(),
  target_record_type: z.string().nullable().optional(),
  target_record_id: z.string().nullable().optional(),
  detected_payload: z.record(z.unknown()).nullable().optional(),
  resolved_by: z.string().nullable().optional(),
  resolved_at: z.string().nullable().optional(),
  resolution_note: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  /** Normalized presentation object (spec-13 §4.1); absent on old payloads. */
  presentation: z.custom<IssuePresentation>().nullable().optional(),
});
export type ExceptionReportDto = z.infer<typeof exceptionReportSchema>;

export const exceptionCountsSchema = z.object({
  info: z.number(),
  warning: z.number(),
  error: z.number(),
  critical: z.number(),
});
export type ExceptionCounts = z.infer<typeof exceptionCountsSchema>;

export const reconciliationSnapshotSchema = z.object({
  reconciliation_snapshot_id: z.string(),
  monthly_batch_id: z.string(),
  run_at: z.string(),
  run_by: z.string(),
  result: z.string(),
  source_available_kg: z.string(),
  source_accounted_kg: z.string(),
  source_diff_kg: z.string(),
  source_buckets: z.record(z.unknown()),
  material_inventory_payload: z.record(z.unknown()),
  summary_comparison_payload: z.record(z.unknown()).nullable().optional(),
  tolerance_settings_snapshot: z.record(z.unknown()),
  exception_count_by_severity: z.record(z.unknown()),
  engine_version: z.string(),
});
export type ReconciliationSnapshotDto = z.infer<typeof reconciliationSnapshotSchema>;

export const reconciliationResultDtoSchema = z.object({
  status: z.string(),
  snapshot_id: z.string(),
  source_available_kg: z.string(),
  source_accounted_kg: z.string(),
  source_diff_kg: z.string(),
  source_buckets: z.record(z.unknown()),
  material_inventory_payload: z.record(z.unknown()),
  summary_comparison_payload: z.record(z.unknown()).nullable().optional(),
  exception_counts: exceptionCountsSchema,
  engine_version: z.string(),
});
export type ReconciliationResultDto = z.infer<typeof reconciliationResultDtoSchema>;

export const massBalanceViewSchema = z.object({
  monthly_batch_id: z.string(),
  batch_status: z.string(),
  latest_snapshot: reconciliationSnapshotSchema.nullable(),
  open_exceptions: z.array(exceptionReportSchema),
  close_readiness: z.string(),
});
export type MassBalanceViewDto = z.infer<typeof massBalanceViewSchema>;

/** Material row inside a reconciliation snapshot's material_inventory_payload. */
export interface MaterialInventoryRow {
  material_id: string;
  material_name: string;
  opening_kg: string;
  production_kg: string;
  adjustment_in_kg: string;
  shipment_kg: string;
  secondary_kg: string;
  closing_kg: string;
  loss_kg: string;
  available_kg: string;
  accounted_kg: string;
  diff_kg: string;
}

/** Summary vs detail per-category row. */
export interface SummaryCategoryRow {
  /** 'inbound' | 'dismantling' — backend tags each row with the source table. */
  table?: 'inbound' | 'dismantling';
  category: string;
  summary_kg: string;
  detail_kg: string;
  diff_kg: string;
}
